import { Card } from '@lingoping/core';

/** A card made from the old bundled Basic German deck (curated text and translations). */
function isCuratedDeckCard(card: Card): boolean {
  return card.linguistic_meta?.content_source === 'deck';
}

/** Which of two cards for the same word to keep: more review progress first, then the curated
 * deck version, then the older card. */
function better(a: Card, b: Card): Card {
  if (a.reps !== b.reps) return a.reps > b.reps ? a : b;
  // A card whose text is prepared beats a pending copy of the same word.
  if ((a.content_status === 'pending') !== (b.content_status === 'pending')) {
    return a.content_status === 'pending' ? b : a;
  }
  if (isCuratedDeckCard(a) !== isCuratedDeckCard(b)) return isCuratedDeckCard(a) ? a : b;
  return new Date(a.created_at).getTime() <= new Date(b.created_at).getTime() ? a : b;
}

/** What makes two cards "the same card": language, word and which meaning of the word. */
export function cardKey(
  card: Pick<Card, 'target_language' | 'lemma' | 'front_text' | 'sense_key'>
) {
  const word = (card.lemma ?? card.front_text).trim().toLowerCase();
  return `${card.target_language}:${word}:${card.sense_key ?? ''}`;
}

/**
 * One card per word, language and meaning. Duplicates appeared when a word was added in an
 * inflected form ("Schiffe") and the AI returned a word that was already a card ("Schiff"). Two
 * cards of one spelling with different meanings ("Bank": bench, bank) are both kept.
 */
export function dedupeCards(cards: Card[]): { kept: Card[]; removed: Card[] } {
  const best = new Map<string, Card>();
  for (const card of cards) {
    const key = cardKey(card);
    const current = best.get(key);
    best.set(key, current ? better(current, card) : card);
  }
  const keepIds = new Set([...best.values()].map((c) => c.id));
  return {
    kept: cards.filter((c) => keepIds.has(c.id)),
    removed: cards.filter((c) => !keepIds.has(c.id)),
  };
}
